import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, User, Share2, Link as LinkIcon, Check } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social media icons
const XIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const FacebookIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

// Share button component with tracking
const ShareButton = ({ title, url, itemType = 'blog' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    // Use /api/og/ URLs for social media sharing (proper og: tags)
    // Regular page URL for copy to clipboard
    const pageUrl = `https://hypnosismasteryseries.com${url}`;
    const ogUrl = `https://hypnosismasteryseries.com/api/og${url}`;
    const encodedOgUrl = encodeURIComponent(ogUrl);
    const encodedTitle = encodeURIComponent(title);
    
    // Extract slug from URL (e.g., /blog/my-post -> my-post)
    const slug = url.split('/').pop();

    // Track share event
    const trackShare = async (platform) => {
        try {
            await axios.post(`${API}/track-share`, {
                item_slug: slug,
                item_type: itemType,
                platform: platform
            });
        } catch (err) {
            // Don't block sharing if tracking fails
            console.log('Share tracking failed:', err);
        }
    };

    const shareLinks = [
        { name: 'X', Icon: XIcon, href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedOgUrl}`, platform: 'twitter' },
        { name: 'Facebook', Icon: FacebookIcon, href: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}`, platform: 'facebook' },
        { name: 'LinkedIn', Icon: LinkedInIcon, href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedOgUrl}&title=${encodedTitle}`, platform: 'linkedin' }
    ];

    const handleShareClick = (platform, href) => {
        trackShare(platform);
        setIsOpen(false);
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(pageUrl);
            setCopied(true);
            trackShare('copy');
            toast.success('Link copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Failed to copy link');
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: title, url: ogUrl });
                trackShare('native');
            } catch (err) {
                // User cancelled or error
            }
        } else {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={handleNativeShare}
                className="inline-flex items-center gap-2 px-4 py-2 text-nature-800/70 hover:text-copper-600 font-body text-sm transition-colors rounded-full hover:bg-nature-100"
                data-testid="share-button"
            >
                <Share2 className="w-4 h-4" />
                Share
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-nature-100 p-2 z-50 min-w-[180px]">
                        {shareLinks.map((link) => (
                            <button
                                key={link.name}
                                onClick={() => handleShareClick(link.platform, link.href)}
                                className="flex items-center gap-3 px-3 py-2 text-nature-800 hover:bg-nature-50 rounded-lg transition-colors font-body text-sm w-full text-left"
                            >
                                <link.Icon />
                                {link.name}
                            </button>
                        ))}
                        <button
                            onClick={copyToClipboard}
                            className="flex items-center gap-3 px-3 py-2 text-nature-800 hover:bg-nature-50 rounded-lg transition-colors font-body text-sm w-full"
                        >
                            {copied ? <Check className="w-5 h-5 text-green-500" /> : <LinkIcon className="w-5 h-5" />}
                            {copied ? 'Copied!' : 'Copy link'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await axios.get(`${API}/blog/posts/${slug}`);
                setPost(response.data);
                // Track view after successfully loading the post
                axios.post(`${API}/blog/posts/${slug}/view`).catch(() => {});
            } catch (err) {
                setError('Post not found');
            } finally {
                setLoading(false);
            }
        };
        fetchPost();
    }, [slug]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Parse inline markdown links [text](url) within text
    const parseInlineLinks = (text) => {
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = linkRegex.exec(text)) !== null) {
            // Add text before the link
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            // Add the link
            const [, linkText, url] = match;
            parts.push(
                <a 
                    key={match.index} 
                    href={url} 
                    className="text-copper-600 hover:text-copper-700 underline"
                >
                    {linkText}
                </a>
            );
            lastIndex = match.index + match[0].length;
        }
        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }
        
        return parts.length > 0 ? parts : text;
    };

    // Convert markdown-like content to HTML
    const renderContent = (content) => {
        if (!content) return null;
        
        // Remove HTML comments (they stay in source for bots, but aren't rendered)
        const contentWithoutComments = content.replace(/<!--[\s\S]*?-->/g, '');
        
        // Split by double newlines for paragraphs
        const paragraphs = contentWithoutComments.split('\n\n').filter(p => p.trim());
        
        return paragraphs.map((para, index) => {
            // Check for markdown images: ![alt](url)
            const imageMatch = para.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
            if (imageMatch) {
                const [, alt, url] = imageMatch;
                return (
                    <figure key={index} className="my-8">
                        <img 
                            src={url} 
                            alt={alt} 
                            className="w-full rounded-lg shadow-lg"
                        />
                        {alt && (
                            <figcaption className="text-center text-sm text-nature-600 mt-3 italic font-body">
                                {alt}
                            </figcaption>
                        )}
                    </figure>
                );
            }
            
            // Check for headers
            if (para.startsWith('## ')) {
                return (
                    <h2 key={index} className="font-heading text-2xl text-nature-900 mt-8 mb-4">
                        {para.replace('## ', '')}
                    </h2>
                );
            }
            if (para.startsWith('### ')) {
                return (
                    <h3 key={index} className="font-heading text-xl text-nature-900 mt-6 mb-3">
                        {para.replace('### ', '')}
                    </h3>
                );
            }
            
            // Check for bullet lists
            if (para.includes('\n- ')) {
                const lines = para.split('\n');
                return (
                    <ul key={index} className="list-disc list-inside space-y-2 my-4 text-nature-800/80 font-body">
                        {lines.map((line, i) => (
                            line.startsWith('- ') ? (
                                <li key={i}>{parseInlineLinks(line.replace('- ', ''))}</li>
                            ) : (
                                <p key={i} className="mb-2">{parseInlineLinks(line)}</p>
                            )
                        ))}
                    </ul>
                );
            }
            
            // Regular paragraph
            return (
                <p key={index} className="text-nature-800/80 font-body leading-relaxed mb-4">
                    {parseInlineLinks(para)}
                </p>
            );
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24">
                <div className="max-w-3xl mx-auto px-6 py-16">
                    <div className="h-8 bg-nature-100 rounded w-3/4 mb-6 skeleton" />
                    <div className="h-4 bg-nature-100 rounded w-1/4 mb-8 skeleton" />
                    <div className="space-y-4">
                        <div className="h-4 bg-nature-100 rounded w-full skeleton" />
                        <div className="h-4 bg-nature-100 rounded w-full skeleton" />
                        <div className="h-4 bg-nature-100 rounded w-2/3 skeleton" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24">
                <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                    <h1 className="font-heading text-3xl text-nature-900 mb-4">Post Not Found</h1>
                    <p className="text-nature-800/60 font-body mb-8">
                        The article you're looking for doesn't exist.
                    </p>
                    <Link 
                        to="/blog"
                        className="inline-flex items-center text-copper-600 font-body font-medium hover:text-copper-700"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Blog
                    </Link>
                </div>
            </div>
        );
    }

    // Extract first image from post content for og:image
    const getPostImage = () => {
        if (!post?.content) return null;
        const imageMatch = post.content.match(/!\[[^\]]*\]\(([^)]+)\)/);
        return imageMatch ? imageMatch[1] : null;
    };

    // Extract FAQ items from post content for FAQPage schema
    const extractFAQItems = () => {
        if (!post?.content) return [];
        
        const faqs = [];
        const content = post.content;
        
        // Pattern 1: Q: and A: on the same line or separate lines
        // Matches: "Q: question? A: answer" or "Q: question\nA: answer"
        const qaInlinePairs = content.matchAll(/Q:\s*([^?\n]+\??)\s*A:\s*([^\n]+(?:\n(?!Q:)[^\n]+)*)/gi);
        for (const match of qaInlinePairs) {
            faqs.push({
                question: match[1].trim(),
                answer: match[2].trim().replace(/\n+/g, ' ')
            });
        }
        
        if (faqs.length > 0) return faqs;
        
        // Pattern 2: Q: / A: on separate lines
        const qaSeparatePairs = content.matchAll(/Q:\s*([^\n]+)\s*\nA:\s*([^\n]+(?:\n(?!Q:)[^\n]+)*)/gi);
        for (const match of qaSeparatePairs) {
            faqs.push({
                question: match[1].trim(),
                answer: match[2].trim().replace(/\n+/g, ' ')
            });
        }
        
        if (faqs.length > 0) return faqs;
        
        // Look for FAQ section (## FAQ, ## Frequently Asked Questions, etc.)
        const faqSectionMatch = content.match(/##\s*(FAQ|Frequently Asked Questions|Common Questions)[^\n]*\n([\s\S]*?)(?=\n##\s|$)/i);
        
        if (faqSectionMatch) {
            const faqContent = faqSectionMatch[2];
            
            // Pattern 3: **Q: Question** followed by answer
            const qaPairs = faqContent.matchAll(/\*\*Q:\s*([^*]+)\*\*\s*\n+([^*\n][^\n]*(?:\n(?!\*\*Q:)[^\n]+)*)/gi);
            for (const match of qaPairs) {
                faqs.push({
                    question: match[1].trim(),
                    answer: match[2].trim().replace(/\n+/g, ' ')
                });
            }
            
            // Pattern 4: **Question?** followed by answer on next line(s)
            if (faqs.length === 0) {
                const boldQuestions = faqContent.matchAll(/\*\*([^*]+\?)\*\*\s*\n+([^*\n][^\n]*(?:\n(?!\*\*)[^\n]+)*)/g);
                for (const match of boldQuestions) {
                    faqs.push({
                        question: match[1].trim(),
                        answer: match[2].trim().replace(/\n+/g, ' ')
                    });
                }
            }
            
            // Pattern 5: ### Question? followed by answer
            if (faqs.length === 0) {
                const h3Questions = faqContent.matchAll(/###\s*([^\n]+\?)\s*\n+([^#\n][^\n]*(?:\n(?!###)[^\n]+)*)/g);
                for (const match of h3Questions) {
                    faqs.push({
                        question: match[1].trim(),
                        answer: match[2].trim().replace(/\n+/g, ' ')
                    });
                }
            }
        }
        
        return faqs;
    };

    const faqItems = extractFAQItems();

    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <SEO 
                title={post.seo_title || post.title}
                description={post.seo_description || post.excerpt}
                canonical={`https://hypnosismasteryseries.com/blog/${post.slug}`}
                type="article"
                image={getPostImage()}
            />
            
            {/* Article Schema.org Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": post.title,
                    "description": post.excerpt,
                    "author": {
                        "@type": "Person",
                        "name": post.author || "Eric Paris",
                        "jobTitle": "Certified Hypnotherapist & NLP Practitioner"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Hypnosis Mastery Series",
                        "url": "https://hypnosismasteryseries.com"
                    },
                    "datePublished": post.created_at,
                    "dateModified": post.updated_at || post.created_at,
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": `https://hypnosismasteryseries.com/blog/${post.slug}`
                    }
                })}
            </script>
            
            {/* FAQPage Schema - only if FAQ items found */}
            {faqItems.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqItems.map(faq => ({
                            "@type": "Question",
                            "name": faq.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": faq.answer
                            }
                        }))
                    })}
                </script>
            )}
            
            <article className="max-w-3xl mx-auto px-6 py-16">
                {/* Back Link */}
                <Link 
                    to="/blog"
                    className="inline-flex items-center text-nature-800/60 font-body text-sm hover:text-copper-600 transition-colors mb-8"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Blog
                </Link>

                {/* Header */}
                <header className="mb-12">
                    <h1 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 leading-tight mb-6 opacity-0 animate-fade-in-up">
                        {post.title}
                    </h1>
                    
                    <div className="flex flex-wrap items-center justify-between gap-4 opacity-0 animate-fade-in-up stagger-1">
                        <div className="flex flex-wrap items-center gap-4 text-nature-800/60 text-sm font-body">
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                {post.author}
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                {formatDate(post.created_at)}
                            </div>
                        </div>
                        <ShareButton title={post.title} url={`/blog/${post.slug}`} />
                    </div>
                </header>

                {/* Content */}
                <div className="prose prose-nature max-w-none opacity-0 animate-fade-in-up stagger-2">
                    {renderContent(post.content)}
                </div>

                {/* CTA */}
                <div className="mt-16 p-8 bg-nature-100 rounded-2xl text-center">
                    <h3 className="font-heading text-xl text-nature-900 mb-3">
                        Ready to Experience Hypnotherapy?
                    </h3>
                    <p className="text-nature-800/70 font-body mb-6">
                        Explore our collection of guided hypnosis sessions.
                    </p>
                    <Link 
                        to="/recordings"
                        className="inline-flex items-center px-6 py-3 bg-copper-500 hover:bg-copper-600 text-white font-body font-medium rounded-full transition-colors"
                    >
                        Browse Recordings
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default BlogPost;
